import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Alle invoervelden gebruiken LOKALE state en committen pas bij blur (of na AI-verbetering).
// Zo vuurt er niet bij elke toetsaanslag een globale update → geen haperingen/verloren toetsaanslagen.
// De OPSLAG blijft ongewijzigd: commit roept gewoon de bestaande update()/updateInvest() aan.
const Block = ({ title, children, cols = "md:grid-cols-3" }) => (
  <div className="mb-5">
    <div className="text-xs font-bold uppercase tracking-wide text-[#1C64B0] mb-2">{title}</div>
    <div className={`grid grid-cols-1 ${cols} gap-3`}>{children}</div>
  </div>
);

// Tekst/number-veld met lokale state, commit op blur.
function DraftInput({ initial, onCommit, testid, className, ...rest }) {
  const [v, setV] = useState(initial ?? "");
  const seen = useRef(initial); const foc = useRef(false);
  useEffect(() => { if (!foc.current && seen.current !== initial) { seen.current = initial; setV(initial ?? ""); } }, [initial]);
  return <input data-testid={testid} value={v} onFocus={() => { foc.current = true; }} onChange={(e) => setV(e.target.value)} onBlur={() => { foc.current = false; seen.current = v; if ((initial ?? "") !== v) onCommit(v); }} className={className} {...rest} />;
}

// Toelichting-tekstveld met lokale state + AI-copilot die alleen de SELECTIE (of anders de hele tekst) verbetert.
function ToelichtingArea({ initial, onCommit, label, onImprove, testid, aiTestid }) {
  const [v, setV] = useState(initial ?? "");
  const [busy, setBusy] = useState(false);
  const seen = useRef(initial);
  const ref = useRef(null);
  const foc = useRef(false);
  useEffect(() => { if (!foc.current && seen.current !== initial) { seen.current = initial; setV(initial ?? ""); } }, [initial]);
  const commit = (val) => { if ((initial ?? "") !== val) onCommit(val); };
  const verbeter = async () => {
    const el = ref.current;
    const full = v;
    let s = 0, e = full.length, sel = false;
    if (el && el.selectionStart != null && el.selectionEnd > el.selectionStart) { s = el.selectionStart; e = el.selectionEnd; sel = true; }
    const target = full.slice(s, e).trim();
    if (!target) { toast.error("Typ of selecteer eerst wat tekst om te verbeteren."); return; }
    setBusy(true);
    try {
      const imp = await onImprove(target);
      if (imp && imp.trim()) {
        const nv = sel ? (full.slice(0, s) + imp.trim() + full.slice(e)) : imp.trim();
        setV(nv); commit(nv);
        toast.success(sel ? "Geselecteerde tekst verbeterd." : "Tekst verbeterd door de AI-copilot.");
      } else toast.error("Geen verbeterde tekst ontvangen.");
    } catch (err) { toast.error("Verbeteren mislukt: " + (err.response?.data?.detail || err.message || "onbekende fout")); }
    finally { setBusy(false); }
  };
  return (
    <label className="block">
      <span className="flex items-center justify-between gap-2 mb-0.5">
        <span className="text-[11px] font-medium text-slate-500">{label}</span>
        <button type="button" data-testid={aiTestid} disabled={busy} onClick={verbeter} className="inline-flex items-center gap-1 rounded border border-[#1C64B0] px-1.5 py-0.5 text-[10px] font-semibold text-[#1C64B0] hover:bg-blue-50 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Verbeter spelling
        </button>
      </span>
      <textarea ref={ref} data-testid={testid} rows={2} value={v} disabled={busy} onFocus={() => { foc.current = true; }} onChange={(e) => setV(e.target.value)} onBlur={() => { foc.current = false; seen.current = v; commit(v); }} className="w-full rounded border border-slate-200 px-2 py-1 text-sm focus:border-[#1C64B0] focus:outline-none disabled:bg-slate-50" />
      <span className="mt-0.5 block text-[10px] text-slate-400">Tip: selecteer een stuk tekst om alléén dat te laten verbeteren; zonder selectie wordt de hele toelichting verbeterd.</span>
    </label>
  );
}

const TOEL_SECTIES = [
  ["productie", "Productie"], ["yield", "Yield & rendement"], ["financieel", "Financieel"],
  ["kosten", "Kosten"], ["kostprijsopbouw", "Kostprijsopbouw"], ["personeel", "Personeel"],
  ["kwaliteit", "Kwaliteit (QA)"], ["techniek", "Technische dienst"], ["voorraad", "Voorraad & inkoop"],
  ["investeringen", "Investeringen & projecten"], ["trends", "Trends"],
];
const TREND_KPIS = [
  ["kg", "Productie"], ["kostprijsKg", "Kostprijs/kg"], ["yieldPct", "Yield"], ["oee", "OEE"],
  ["uitvalPct", "Uitval"], ["omzet", "Omzet"], ["marge", "Marge"], ["kgPerManuur", "kg/manuur"], ["persKostPerKg", "Pers.kosten/kg"],
];
const INV_STATUS = ["Gepland", "Lopend", "Afgerond", "Uitgesteld"];

export default function DirectieManualPanel({ man, canEdit, effW, yr, update, updateInvest }) {
  const [open, setOpen] = useState(false);
  if (!canEdit || !effW) return null;
  const m = man || {};
  const tCfg = m.trendCfg || {};

  const improve = (label) => (t) => api.post("/rapportage/verbeter-tekst", { tekst: t, context: label }).then((r) => r.data?.verbeterd || "");

  const num = (section, fld, label, { pct = false, hint = "" } = {}) => (
    <label key={`${section}-${fld}`} className="block">
      <span className="block text-[11px] font-medium text-slate-500 mb-0.5">{label}{pct ? " (%)" : ""}</span>
      <DraftInput testid={`wkrap-${section}-${fld}`} type="number" step="any" initial={(m[section] || {})[fld] ?? ""} onCommit={(v) => update(section, { [fld]: v })} className="w-full rounded border border-slate-200 px-2 py-1 text-sm text-right focus:border-[#1C64B0] focus:outline-none" />
      {hint ? <span className="block text-[10px] text-slate-400 mt-0.5">{hint}</span> : null}
    </label>
  );

  const toel = (section, fld, label, span = "") => (
    <div key={`${section}-${fld}`} className={span}>
      <ToelichtingArea testid={`wkrap-${section}-${fld}`} aiTestid={`ai-verbeter-${section}-${fld}`} label={label} initial={(m[section] || {})[fld] ?? ""} onCommit={(v) => update(section, { [fld]: v })} onImprove={improve(label)} />
    </div>
  );

  const inv = Array.isArray(m.investeringen) ? m.investeringen : [];
  const setInv = (i, patch) => updateInvest(inv.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addInv = () => updateInvest([...inv, { onderwerp: "", status: "Gepland", begroot: "", werkelijk: "", verantwoordelijke: "", deadline: "", actie: "" }]);
  const delInv = (i) => updateInvest(inv.filter((_, idx) => idx !== i));

  return (
    <div className="mb-6 rounded-lg border border-slate-200 bg-white" data-testid="dir-manual-panel">
      <button data-testid="dir-manual-toggle" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Handmatige toelichting — week {effW} · {yr}
        </span>
        <span className="text-xs text-slate-400">toelichting per onderdeel, trends, QA, techniek, projecten · opgeslagen per week</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-4">
          <p className="mb-4 text-xs text-slate-500">Wordt veilig per week bewaard en verschijnt alleen in het <b>uitgebreide</b> verslag (onderaan elk blok). Tekst wordt pas vastgelegd zodra je het veld verlaat. Met <b>"Verbeter spelling"</b> corrigeert de AI-copilot spelling en zinsopbouw — selecteer eerst een stuk tekst om alleen dat deel te verbeteren.</p>

          <Block title="Toelichting per onderdeel (verschijnt onderaan de betreffende sectie in het verslag)" cols="md:grid-cols-1">
            {TOEL_SECTIES.map(([k, lbl]) => toel("toelichting", k, lbl))}
          </Block>

          <div className="mb-5">
            <div className="text-xs font-bold uppercase tracking-wide text-[#1C64B0] mb-2">Trend-instellingen</div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-600">Trend toont laatste
                <select data-testid="wkrap-trend-weken" value={tCfg.weken ?? 8} onChange={(e) => update("trendCfg", { weken: Number(e.target.value) })} className="rounded border border-slate-300 px-2 py-1 text-sm">
                  {[4, 8, 13, 26, 52].map((n) => <option key={n} value={n}>{n} weken</option>)}
                </select>
              </label>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-sm text-slate-500">KPI's in trend:</span>
                {TREND_KPIS.map(([k, lbl]) => {
                  const on = (tCfg.kpis || {})[k] !== false;
                  return <label key={k} className="flex items-center gap-1 text-sm text-slate-600 cursor-pointer"><input type="checkbox" data-testid={`wkrap-trend-kpi-${k}`} checked={on} onChange={(e) => update("trendCfg", { kpis: { ...(tCfg.kpis || {}), [k]: e.target.checked } })} className="h-4 w-4 accent-[#1C64B0] cursor-pointer" /> {lbl}</label>;
                })}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Bepaalt vanaf hoeveel weken de trend wordt getoond en welke KPI's meegaan (bijv. marge weglaten). Geldt voor het uitgebreide verslag.</p>
          </div>

          <Block title="Normen (voor kleuren & vergelijking)">
            {num("normen", "kg", "Geproduceerd (kg)")}
            {num("normen", "margePct", "Marge", { pct: true })}
            {num("normen", "kostprijsKg", "Kostprijs/kg (€)")}
            {num("normen", "yieldPct", "Yield", { pct: true })}
            {num("normen", "uitvalPct", "Uitval", { pct: true })}
            {num("normen", "oee", "OEE", { pct: true })}
            {num("normen", "kgPerUur", "kg / productie-uur")}
            {num("normen", "persKostPerKg", "Pers.kosten/kg (€)")}
          </Block>

          <Block title="Personeel (aanvullend)">
            {num("pers", "fte", "Actieve FTE (eigen)")}
            {num("pers", "uzkAantal", "Uitzendkrachten")}
            {num("pers", "overurenUur", "Overuren (u)")}
            {num("pers", "ziekteverzuim", "Ziekteverzuim", { pct: true })}
            {num("pers", "vakantie", "Vakantie (u)")}
          </Block>

          <Block title="Extra kosten (handmatig, €)">
            {num("kosten", "verpakking", "Verpakking")}
            {num("kosten", "transport", "Transport")}
            {num("kosten", "onderhoud", "TD / onderhoud")}
            {num("kosten", "uzk", "Uitzendkrachten")}
            {num("kosten", "overigExtra", "Overige productiekosten")}
          </Block>

          <Block title="Financieel">
            {num("financieel", "budget", "Omzetbudget / doelstelling (€)", { hint: "voor 'omzet vs budget'" })}
          </Block>

          <Block title="Kwaliteit (QA)">
            {num("kwaliteit", "klachten", "Aantal klachten")}
            {num("kwaliteit", "afwijkingen", "Interne afwijkingen")}
            {num("kwaliteit", "rework", "Rework")}
            {num("kwaliteit", "geblokkeerd", "Geblokkeerde producten")}
            {num("kwaliteit", "micro", "Microbiologische afwijkingen")}
            <div className="hidden md:block" />
            {toel("kwaliteit", "qaActies", "Openstaande QA-acties")}
            {toel("kwaliteit", "audits", "Audits / bijzonderheden")}
          </Block>

          <Block title="Technische dienst">
            {num("techniek", "storingen", "Aantal storingen")}
            {num("techniek", "stilstandUur", "Totale stilstand (u)")}
            {num("techniek", "openReparaties", "Openstaande reparaties")}
            {num("techniek", "preventief", "Preventief onderhoud")}
            {num("techniek", "kostenTD", "Kosten TD/onderhoud (€)")}
            <div className="hidden md:block" />
            {toel("techniek", "hoofdstoring", "Belangrijkste storing(en)")}
            {toel("techniek", "punten", "Technische punten die productie beïnvloeden")}
          </Block>

          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wide text-[#1C64B0]">Investeringen &amp; projecten <span className="normal-case font-normal text-slate-400">· ook geplande (toekomstige) investeringen: kies status "Gepland" en vul de geplande datum/periode in</span></div>
              <button data-testid="wkrap-invest-add" onClick={addInv} className="inline-flex items-center gap-1 rounded-md border border-[#1C64B0] px-2 py-1 text-xs font-semibold text-[#1C64B0] hover:bg-blue-50"><Plus className="h-3.5 w-3.5" /> Regel</button>
            </div>
            {inv.length === 0 ? <p className="text-xs text-slate-400">Nog geen projecten of geplande investeringen vastgelegd.</p> : (
              <div className="space-y-2">
                {inv.map((r, i) => (
                  <div key={i} data-testid={`wkrap-invest-row-${i}`} className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 p-2 md:grid-cols-7">
                    <DraftInput testid={`wkrap-invest-onderwerp-${i}`} placeholder="Onderwerp" initial={r.onderwerp ?? ""} onCommit={(v) => setInv(i, { onderwerp: v })} className="rounded border border-slate-200 px-2 py-1 text-sm md:col-span-2" />
                    <select data-testid={`wkrap-invest-status-${i}`} value={r.status ?? ""} onChange={(e) => setInv(i, { status: e.target.value })} className="rounded border border-slate-200 px-2 py-1 text-sm">
                      <option value="">Status…</option>
                      {INV_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <DraftInput type="number" step="any" placeholder="Begroot" initial={r.begroot ?? ""} onCommit={(v) => setInv(i, { begroot: v })} className="rounded border border-slate-200 px-2 py-1 text-right text-sm" />
                    <DraftInput type="number" step="any" placeholder="Werkelijk" initial={r.werkelijk ?? ""} onCommit={(v) => setInv(i, { werkelijk: v })} className="rounded border border-slate-200 px-2 py-1 text-right text-sm" />
                    <DraftInput placeholder="Verantw." initial={r.verantwoordelijke ?? ""} onCommit={(v) => setInv(i, { verantwoordelijke: v })} className="rounded border border-slate-200 px-2 py-1 text-sm" />
                    <div className="flex gap-1">
                      <DraftInput testid={`wkrap-invest-deadline-${i}`} placeholder="Deadline / geplande datum (bv. december 2026)" initial={r.deadline ?? ""} onCommit={(v) => setInv(i, { deadline: v })} className="w-full rounded border border-slate-200 px-2 py-1 text-sm" />
                      <button data-testid={`wkrap-invest-del-${i}`} onClick={() => delInv(i)} className="shrink-0 rounded-md border border-rose-200 px-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <DraftInput placeholder="Actie / besluit" initial={r.actie ?? ""} onCommit={(v) => setInv(i, { actie: v })} className="rounded border border-slate-200 px-2 py-1 text-sm md:col-span-7" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <Block title="Managementacties" cols="md:grid-cols-1">
            {toel("acties", "afwijkingen", "Belangrijkste afwijkingen deze week")}
            {toel("acties", "oorzaak", "Oorzaak")}
            {toel("acties", "actiesKomend", "Genomen actie / acties komende week")}
            {toel("acties", "besluiten", "Besluit / ondersteuning nodig van directie")}
          </Block>
        </div>
      )}
    </div>
  );
}
